import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Modal, TextInput, Textarea } from "@opendate-app/setlist";
import { formatDateTime } from "../giftCardLabels";

const deliveryStatusFor = (giftCard) => {
  if (giftCard.deliveredAt) return `Delivered ${formatDateTime(giftCard.deliveredAt)}.`;
  if (giftCard.deliverAt) return `Scheduled to be sent ${giftCard.deliverAtLabel || formatDateTime(giftCard.deliverAt)}.`;
  return "This card's email hasn't been delivered yet.";
};

const warningFor = (giftCard, changingRecipient) => {
  if (!giftCard.deliveredAt) {
    return {
      title: "Nothing is sent now",
      description: "This card hasn't been delivered yet, so the new recipient gets it when it is sent.",
    };
  }
  if (changingRecipient) {
    return {
      title: "This sends a new code",
      description: `The new recipient gets a new code, the old code ending ${giftCard.last4} stops working right away, and ${giftCard.ownerEmail} is told the card was moved.`,
    };
  }
  return {
    title: "This sends a new code",
    description: `The email carries a new code and the old code ending ${giftCard.last4} stops working right away.`,
  };
};

const GiftCardResendModal = ({
  giftCard,
  canResend,
  canChangeRecipient,
  resendRefusal,
  submitting,
  onClose,
  onResend,
  onChangeRecipient,
}) => {
  const [recipientName, setRecipientName] = useState(giftCard.recipientName || "");
  const [recipientEmail, setRecipientEmail] = useState(giftCard.ownerEmail || "");
  const [note, setNote] = useState("");

  const email = recipientEmail.trim();
  const changingRecipient =
    email.toLowerCase() !== (giftCard.ownerEmail || "").toLowerCase() ||
    recipientName.trim() !== (giftCard.recipientName || "");
  const ready = changingRecipient
    ? !submitting && Boolean(email) && Boolean(note.trim())
    : !submitting && canResend;
  const warning = warningFor(giftCard, changingRecipient);

  const submit = () =>
    changingRecipient
      ? onChangeRecipient({ recipientName: recipientName.trim(), recipientEmail: email, note })
      : onResend();

  const footer = (
    <div className="gift-card-modal-footer">
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" disabled={!ready} onClick={submit}>
        {changingRecipient ? "Move to new recipient" : "Resend email"}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      icon={false}
      title={canChangeRecipient ? "Change recipient or resend" : "Resend gift email"}
      description={`Gift email for •••• ${giftCard.last4}`}
      footer={footer}
    >
      <div className="gift-card-modal-body">
        {canChangeRecipient ? (
          <>
            <TextInput
              label="Recipient name"
              value={recipientName}
              onChange={(event) => setRecipientName(event.target.value)}
            />
            <TextInput
              label="Recipient email"
              type="email"
              required
              value={recipientEmail}
              onChange={(event) => setRecipientEmail(event.target.value)}
            />
            {changingRecipient && (
              <Textarea
                label="Note (required)"
                rows={3}
                placeholder="Why is the recipient changing?"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </>
        ) : (
          <p className="gift-card-detail-muted">{`Sends the gift email to ${giftCard.ownerEmail} again.`}</p>
        )}
        <p className="gift-card-detail-muted">{deliveryStatusFor(giftCard)}</p>
        {!canResend && !changingRecipient && resendRefusal && (
          <p className="gift-card-detail-muted">{resendRefusal}</p>
        )}
        <Alert color="warning" size="full-width" title={warning.title} description={warning.description} />
      </div>
    </Modal>
  );
};

GiftCardResendModal.propTypes = {
  giftCard: PropTypes.shape({
    last4: PropTypes.string.isRequired,
    ownerEmail: PropTypes.string,
    recipientName: PropTypes.string,
    deliverAt: PropTypes.string,
    deliveredAt: PropTypes.string,
  }).isRequired,
  canResend: PropTypes.bool.isRequired,
  canChangeRecipient: PropTypes.bool.isRequired,
  resendRefusal: PropTypes.string,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onChangeRecipient: PropTypes.func.isRequired,
};

GiftCardResendModal.defaultProps = {
  resendRefusal: null,
  submitting: false,
};

export default GiftCardResendModal;
